import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { withSnackbar } from '../../components/hocs/withSnackbar';
import {
    BuyerInvoicesFilter,
    BuyerInvoicesTable
} from '../../components/invoices/buyer-invoice';
import { getServiceFeeDisplayValue } from '../../components/invoices/serviceFeeHelper.js';
import { getBuyerInvoices } from '../../requests/reports/invoices/buyer-invoices-requests';
import { getErrorMessageForStandardResponse } from '../../util/NetworkErrorUtil';
import { formatCurrencyValue } from '../../util/PaymentUtils';
import { BookingTransactionTypeLabel, DateFormats } from '../../components/constants/securspace-constants';
import getDisplayValue from '../../components/BuyerTransactionStatus';
import { propertiesToCheck } from '../../components/invoices/buyer-invoice/data';
import moment from 'moment'
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import BuyerInvoicesReportDataSummary from "../../components/invoices/buyer-invoice/BuyerInvoicesReportDataSummary";
import BuyerInvoicesGroupSummary from "../../components/invoices/buyer-invoice/BuyerInvoicesGroupSummary";
import {formatDateMMDDyyyy} from "../../util/DateUtils";
import {SecurSpaceReportTitle} from "../../components/common/reports/SecurSpaceReportTitle";
import Pagination from "../../components/deprecatedUikit/Pagination/Pagination";
import InfoCardLoader from "../../components/deprecatedUikit/InfoCard/InfoCardLoader";

export const toggleItem = (id, setExpandedItems) => {
    setExpandedItems((prevExpandedItems) => {
        const updatedExpandedItems = { ...prevExpandedItems };
        if (updatedExpandedItems[id]) {
            delete updatedExpandedItems[id];
        } else {
            updatedExpandedItems[id] = true;
        }
        return updatedExpandedItems;
    });
};

export const expandAllItems = (items, itemKey, setExpandedItems, expandedAll, setExpandedAll) => {
    const allExpanded = items.reduce((acc, item) => {
        acc[item[itemKey]] = true;
        return acc;
    }, {});

    setExpandedItems(allExpanded);
    setExpandedAll(!expandedAll);
};

const BuyerInvoicesReport = ({ account, snackbarShowMessage }) => {

    const {orderNumber, transactionNumber} = useUrlQueryParams();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(50);
    const [totalNumberResults, setTotalNumberResults] = useState(0);
    const [selectedLocation, setSelectedLocation] = useState();
    const [startDate, setStartDate] = useState(moment().add(-1, 'months').format(DateFormats.DAY));
    const [endDate, setEndDate] = useState(moment().format(DateFormats.DAY));
    const [bookingNumber, setBookingNumber] = useState(orderNumber);
    const [invoiceNumber, setInvoiceNumber] = useState(transactionNumber);
    const [invoiceType, setInvoiceType] = useState('');
    const [status, setStatus] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sortDir, setSortDir] = useState('');
    const [expandedItems, setExpandedItems] = useState({});
    const [expandedAll, setExpandedAll] = useState(false);
    const [query, setQuery] = useState(null);

    const handlePageChange = (_, selectedPage) => {
        setPage(selectedPage)
    };

    const handleSizeChange = (event) => {
        setSize(event.target.value)
    };

    const handleToggle = (id) => toggleItem(id, setExpandedItems);
    const handleExpandAll = () => expandAllItems(invoices, 'id', setExpandedItems, expandedAll, setExpandedAll);

    useEffect(() => {
        const { id } = account;
        if (!id) return;
        const checkLength = (number) => number?.length >= 3 ? number : "";

        setLoading(true)
        const requestParams = {
            startDate: formatDateMMDDyyyy(startDate),
            endDate: formatDateMMDDyyyy(endDate),
            locationId: selectedLocation?.id,
            bookingNumber: checkLength(bookingNumber),
            transactionNumber: checkLength(invoiceNumber),
            transactionType: invoiceType,
            buyerTransactionStatus: status,
            page,
            size,
            sortBy,
            sortDir
        }
        setQuery(requestParams);

        getBuyerInvoices(requestParams)
            .then(({ body }) => {
                const { content } = body;
                const data = content?.map((item) => {

                    const dynamicProperties = {};

                    propertiesToCheck.forEach(property => {
                        dynamicProperties[property] = item[property] ? item[property] : "-";
                    });

                    return {
                        ...item,
                        ...dynamicProperties,
                        buyerAmount: formatCurrencyValue(item.buyerAmount),
                        transactionType: BookingTransactionTypeLabel[item?.transactionType] || item?.transactionType,
                        status: getDisplayValue(item?.status),
                        id: item?.transactionId,
                        serviceFeeType: getServiceFeeDisplayValue(item?.serviceFeeType)
                    }
                });
                setTotalNumberResults(body.count);
                setInvoices(data);
                setExpandedItems({})
            })
            .catch(err => {
                snackbarShowMessage(getErrorMessageForStandardResponse(err), "error", 15000)
            }).finally(() => setLoading(false));
    }, [account, page, size, startDate, endDate, selectedLocation, bookingNumber, invoiceNumber, invoiceType, status, sortBy, sortDir, snackbarShowMessage]);

    return (
        <Grid item container flexDirection={'column'}>
            <SecurSpaceReportTitle mb={4}>Invoices</SecurSpaceReportTitle>
            <BuyerInvoicesFilter
                setSelectedLocation={setSelectedLocation}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                setBookingNumber={setBookingNumber}
                setInvoiceNumber={setInvoiceNumber}
                setSortBy={setSortBy}
                setSortDir={setSortDir}
                setInvoiceType={setInvoiceType}
                setStatus={setStatus}
                orderNumber={orderNumber}
                transactionNumber={transactionNumber}
            />
            <BuyerInvoicesGroupSummary query={query} />
            {
                invoices.length > 0 &&
                <BuyerInvoicesReportDataSummary
                  accountId={account?.id}
                  query={query}
                  title={selectedLocation?.locationName || 'All'}
                  count={invoices?.length}
                  setExpandedItems={setExpandedItems}
                  setExpandedAll={setExpandedAll}
                  isShowCollapseButton={true}
                  handleExpandAll={handleExpandAll}
                  hasExpandedItems={Object.keys(expandedItems).length > 0}
                />
            }
            {
                loading ? [...Array(10).keys()].map((_, index) => (
                    <InfoCardLoader key={index} />
                )) : <BuyerInvoicesTable
                    loading={loading}
                    invoices={invoices}
                    locationId={selectedLocation?.id || ''}
                    invoiceNumber={invoiceNumber}
                    bookingNumber={bookingNumber}
                    invoiceType={invoiceType}
                    sortBy={sortBy}
                    handleToggle={handleToggle}
                    expandedItems={expandedItems}
                />
            }

            {
                invoices.length > 0 && <Grid item>
                    <Pagination
                        count={totalNumberResults}
                        rowsPerPage={size}
                        page={page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleSizeChange}
                        variant="tablePagination"
                        scrollToBottomOnPageChange={true}
                    />
                </Grid>
            }
        </Grid>
    )
}

export default withSnackbar(BuyerInvoicesReport)
